/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reaction, sleep } from '@reatom/framework'

import {
  Array,
  AsyncStateN,
  Duration,
  Iterable,
  Order,
  pipe,
  Record,
  Result,
} from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { HeliusSender } from '@root/entities/network/model/solana'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { isTauriOrBrowserLocalAtom, UnknownError } from '@root/shared'

const PING_INTERVAL = Duration.seconds(10)
const REPROBE_INTERVAL_TICKS = 6

const measurePing = async (
  url: string,
): Promise<Result.Result<number, UnknownError>> => {
  const start = performance.now()
  try {
    const response = await fetch(url)
    if (!response.ok) {
      return Result.fail(UnknownError.fromUnknown(response.status))
    }
    return Result.succeed(Math.round(performance.now() - start))
  } catch (error) {
    return Result.fail(UnknownError.fromUnknown(error))
  }
}

const init: Record<
  HeliusSender.RpcId,
  AsyncStateN.AsyncStateN<UnknownError, { pingTime: number }>
> = {
  default: AsyncStateN.notInitiated(),
  slc: AsyncStateN.notInitiated(),
  ewr: AsyncStateN.notInitiated(),
  lon: AsyncStateN.notInitiated(),
  fra: AsyncStateN.notInitiated(),
  ams: AsyncStateN.notInitiated(),
  sg: AsyncStateN.notInitiated(),
  tyo: AsyncStateN.notInitiated(),
}

const reatomHeliusRpcLinks = () => {
  const rpcPingStateAtom = atom(init)

  const pingAllAction = action(async (ctx) => {
    const isTauri = ctx.get(isTauriOrBrowserLocalAtom)
    if (isTauri) {
      const { secureStorageService } = ctx.get(dependenciesAtom)
      const closestResponse = await ctx.schedule(() =>
        secureStorageService.findClosestSenderLocation(),
      )
      if (Result.isFailure(closestResponse)) {
        const mappedError = UnknownError.fromUnknown(closestResponse.failure)
        rpcPingStateAtom(ctx, {
          default: AsyncStateN.error(mappedError),
          slc: AsyncStateN.error(mappedError),
          ewr: AsyncStateN.error(mappedError),
          lon: AsyncStateN.error(mappedError),
          fra: AsyncStateN.error(mappedError),
          ams: AsyncStateN.error(mappedError),
          sg: AsyncStateN.error(mappedError),
          tyo: AsyncStateN.error(mappedError),
        })
        return
      }

      const updated = { ...init }
      for (const ping of closestResponse.success.pings) {
        const id = ping.location as HeliusSender.RpcId
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!HeliusSender.byId[id]) {
          continue
        }
        updated[id] =
          ping.pingMs === null
            ? AsyncStateN.error(
                UnknownError.fromUnknown(
                  ping.error ?? `Ping failed for location ${ping.location}`,
                ),
              )
            : AsyncStateN.success({ pingTime: ping.pingMs })
      }
      rpcPingStateAtom(ctx, updated)
      return
    }

    /**
     * The endpoints a page is allowed to reach.
     *
     * Only the default one answers over https; the seven regional ones are
     * plain http, which a page served over https refuses to load at all — so
     * in the browser they failed instantly, every cycle, and the fastest
     * region could only ever come out as the default. Measured 2026-10-02:
     * `https://sender.helius-rpc.com/ping` answers in 69ms, each
     * `http://<region>-sender.helius-rpc.com/ping` fails in nought to four,
     * and none of them answers over https at all.
     *
     * Tauri measures these natively, above, where no such rule applies. Here
     * they are left unmeasured rather than measured as broken, which is the
     * honest state and seven fewer doomed requests a cycle.
     */
    const reachable = HeliusSender.all.filter((it) =>
      it.pingUrl.startsWith('https:'),
    )

    const pings = await ctx.schedule(() =>
      Promise.all(
        reachable.map(async (it) => {
          const result = await ctx.schedule(() => measurePing(it.pingUrl))
          return {
            rpc: it,
            result: result.pipe(Result.map((it) => ({ pingTime: it }))),
          }
        }),
      ),
    )

    const updated = { ...init }
    for (const { rpc, result } of pings) {
      updated[rpc.id] = AsyncStateN.fromResult(result)
    }
    rpcPingStateAtom(ctx, updated)
  }, 'pingAllAction')

  const warmFastestAction = action(async (ctx) => {
    const isTauri = ctx.get(isTauriOrBrowserLocalAtom)
    if (!isTauri) {
      return
    }
    const { secureStorageService } = ctx.get(dependenciesAtom)
    const fastest = ctx.get(fastestRpcAtom)
    const warmResult = await ctx.schedule(() =>
      secureStorageService.warmSenderConnection(fastest.id),
    )
    if (Result.isFailure(warmResult)) {
      return
    }

    rpcPingStateAtom(
      ctx,
      pipe(
        ctx.get(rpcPingStateAtom),
        Record.set(
          warmResult.success.location,
          AsyncStateN.success({ pingTime: warmResult.success.pingMs }),
        ),
      ),
    )
  }, 'warmFastestAction')

  const _debug = reaction((ctx) => {
    const state = ctx.spy(rpcPingStateAtom)
    const fastest = ctx.spy(fastestRpcAtom)
    // eslint-disable-next-line no-console
    console.log(state, fastest)
  })

  const initializeAction = action(async (ctx) => {
    const isTauri = ctx.get(isTauriOrBrowserLocalAtom)
    await ctx.schedule(() => pingAllAction(ctx))

    let ticks = 0
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    while (true) {
      await ctx.schedule(() => sleep(Duration.toMillis(PING_INTERVAL)))

      if (isTauri) {
        await ctx.schedule(() => warmFastestAction(ctx))
        ticks += 1
        if (ticks % REPROBE_INTERVAL_TICKS === 0) {
          await ctx.schedule(() => pingAllAction(ctx))
        }
      } else {
        await ctx.schedule(() => pingAllAction(ctx))
      }
    }
  })

  const fastestRpcAtom = atom((ctx): HeliusSender.HeliusRpc => {
    const rpcPingState = ctx.spy(rpcPingStateAtom)

    const fastest = pipe(
      rpcPingState,
      Record.entries,
      Iterable.map(([id, state]) => {
        if (AsyncStateN.isSuccess(state)) {
          return {
            rpc: HeliusSender.byId[id],
            pingTime: state.value.pingTime,
          }
        }
        return undefined
      }),
      Iterable.filter((it) => it !== undefined),
      Iterable.toArray,
      Array.sortWith((it) => it.pingTime, Order.Number),
      Array.first,
    )

    return fastest?.rpc ?? HeliusSender.Default
  })

  return {
    fastest: fastestRpcAtom,
    pingState: rpcPingStateAtom,
    initializeAction,
  }
}

export const heliusRpcLinksModel = reatomHeliusRpcLinks()

export const initializeHeliusRpcLinksAction = reaction((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  const isLoggedIn = ctx.spy(isLoggedInAtom)
  if (network !== Network.Solana || !isLoggedIn) {
    return
  }
  heliusRpcLinksModel.initializeAction(ctx)
}, 'initializeHeliusRpcLinksAction')
